import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, setAuthToken as setGlobalAuthToken } from "@/lib/queryClient";
import { signInWithGoogle, isFirebaseConfigured } from "@/lib/firebase";
import { useStore } from "@/lib/store";
import { queryClient } from "@/lib/queryClient";
import { pushAccountCreated } from "@/lib/gtm";
import {
  submitGoogleLogin,
  type GoogleAuthenticatedResponse,
  type GoogleUser,
} from "@/lib/google-login-flow";

interface GoogleLoginButtonProps {
  onSuccess?: (user: GoogleUser, token: string) => void;
  /**
   * If true, do NOT auto-redirect new Google signups to /trial onboarding.
   * Use on pages where the parent owns the post-login flow (e.g. /checkout
   * → Stripe). Onboarding can still be completed later from the dashboard
   * progress bar.
   */
  skipOnboardingRedirect?: boolean;
}

export function GoogleLoginButton({
  onSuccess,
  skipOnboardingRedirect = false,
}: GoogleLoginButtonProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { setAuthToken } = useStore();

  // VP-313: konta z 2FA (TOTP) dostają z backendu `requires2FA` zamiast tokenu —
  // `idToken` z popupu trzymamy, żeby ponowić to samo żądanie razem z kodem.
  const [pendingIdToken, setPendingIdToken] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [totpError, setTotpError] = useState<string | null>(null);
  const [isVerifyingTotp, setIsVerifyingTotp] = useState(false);

  const closeTotpPrompt = () => {
    setPendingIdToken(null);
    setTotpCode("");
    setTotpError(null);
  };

  const finalizeLogin = (data: GoogleAuthenticatedResponse) => {
    // Store token in auth context (same as email/password login)
    setAuthToken(data.token);
    setGlobalAuthToken(data.token);
    queryClient.setQueryData(["/api/auth/me"], { user: data.user });
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] !== "/api/auth/me",
    });

    toast({
      title: t("googleLogin.signedIn"),
      description: t("googleLogin.welcome", { name: data.user.username }),
    });

    // Tylko swieze konta — logowanie istniejacego usera przez Google to nie konwersja.
    if (data.isNewUser) {
      pushAccountCreated({
        userId: data.user.id,
        email: data.user.email,
        signupMethod: "google_oauth",
      });
    }

    // New Google signups must complete onboarding (phone, company, etc.)
    // — this triggers the n8n webhook which sends the Slack "new trial" notification.
    // Pages owning a post-login flow (e.g. /checkout → Stripe) opt out via skipOnboardingRedirect.
    if (
      data.requiresOnboarding &&
      !skipOnboardingRedirect &&
      window.location.pathname !== "/trial"
    ) {
      window.location.href = "/trial";
      return;
    }

    onSuccess?.(data.user, data.token);
  };

  /** Jedno żądanie do `/api/auth/google` — z kodem TOTP albo bez. */
  const runGoogleLogin = async (idToken: string, code?: string) => {
    const params = new URLSearchParams(window.location.search);
    const fprCode = params.get("fpr");
    const outcome = await submitGoogleLogin({
      post: (body) => apiRequest("POST", "/api/auth/google", body),
      idToken,
      totpCode: code,
      referralCode: fprCode ?? undefined,
    });

    if (outcome.status === "authenticated") {
      closeTotpPrompt();
      finalizeLogin(outcome.data);
      return;
    }

    if (outcome.status === "totp_required") {
      setPendingIdToken(idToken);
      setTotpCode("");
      setTotpError(outcome.invalidCode ? t("googleLogin.totpInvalid") : null);
      return;
    }

    // google_session_expired — `idToken` wygasł/został odrzucony, potrzebny nowy popup.
    closeTotpPrompt();
    toast({
      title: t("googleLogin.errorTitle"),
      description: t("googleLogin.sessionExpired"),
      variant: "destructive",
    });
  };

  const handleGoogleLogin = async () => {
    if (!isFirebaseConfigured()) {
      toast({
        title: t("googleLogin.unavailable"),
        description: t("googleLogin.firebaseHint"),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    // Nie czyscimy `pendingIdToken` z gory: gdy user zamknie popup Google, poprzedni
    // (wciaz wazny) idToken przepadlby razem z okienkiem na kod i bez zadnego komunikatu.
    setTotpError(null);
    try {
      const googleResult = await signInWithGoogle();
      if (!googleResult) return; // popup closed by user

      if (!googleResult.idToken) {
        toast({
          title: t("googleLogin.errorTitle"),
          description: t("googleLogin.errorRetry"),
          variant: "destructive",
        });
        return;
      }

      await runGoogleLogin(googleResult.idToken);
    } catch {
      toast({
        title: t("googleLogin.errorTitle"),
        description: t("googleLogin.errorRetry"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTotpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingIdToken || isVerifyingTotp) return;

    setIsVerifyingTotp(true);
    try {
      await runGoogleLogin(pendingIdToken, totpCode);
    } catch {
      setTotpError(t("googleLogin.errorRetry"));
    } finally {
      setIsVerifyingTotp(false);
    }
  };

  if (!isFirebaseConfigured()) {
    return null;
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={handleGoogleLogin}
        disabled={isLoading}
        data-testid="button-google-login"
      >
        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        {isLoading ? t("googleLogin.signingIn") : t("googleLogin.signIn")}
      </Button>

      <Dialog
        open={pendingIdToken !== null}
        onOpenChange={(open) => {
          if (!open) closeTotpPrompt();
        }}
      >
        <DialogContent className="sm:max-w-sm" data-testid="dialog-google-totp">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-yellow-500" />
              {t("googleLogin.totpTitle")}
            </DialogTitle>
            <DialogDescription>{t("googleLogin.totpDescription")}</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleTotpSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="google-totp-code" className="text-foreground/80">
                {t("googleLogin.totpLabel")}
              </Label>
              <Input
                id="google-totp-code"
                type="text"
                autoComplete="off"
                autoCapitalize="characters"
                value={totpCode}
                onChange={(e) => {
                  // Kod z aplikacji ma 6 cyfr, ale kod zapasowy to 8 znakow HEX (litery!)
                  // — patrz generateBackupCodes() w apps/api/platform/totp-service.ts.
                  // Filtr wylacznie na cyfry blokowalby kody zapasowe obiecane w podpowiedzi.
                  setTotpCode(
                    e.target.value
                      .replace(/[^a-zA-Z0-9]/g, "")
                      .toUpperCase()
                      .slice(0, 8)
                  );
                  setTotpError(null);
                }}
                maxLength={8}
                required
                autoFocus
                className="bg-background border-border text-foreground text-center text-lg tracking-widest"
                placeholder="000000"
                data-testid="input-google-totp"
              />
              <p className="text-muted-foreground text-xs">{t("googleLogin.totpHint")}</p>
            </div>

            {totpError && (
              <p
                className="text-red-600 dark:text-red-400 text-sm"
                data-testid="text-google-totp-error"
              >
                {totpError}
              </p>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isVerifyingTotp || totpCode.length < 6}
              data-testid="button-google-totp-submit"
            >
              {isVerifyingTotp ? t("googleLogin.totpVerifying") : t("googleLogin.totpConfirm")}
            </Button>
          </form>

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={handleGoogleLogin}
            disabled={isVerifyingTotp}
            data-testid="button-google-totp-restart"
          >
            {t("googleLogin.totpRestart")}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
